import { Dialog } from "@headlessui/react";
import { v4 as uuid } from "uuid";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { updateUserEmail } from "service/userservice/updateUserEmail";
import { states } from "js/placeholderData/states";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "redux/slices/userSlice";
import { getAllOwnersFromParties } from "js/utilities";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import FormInputError from "components/General/FormInputError";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";
import { Trans, useTranslation } from "react-i18next";

export default function DEPRECATING_USERINFODIALOG({
  dialogState,
  setDialogState,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const UUID = uuid();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      mobilePhone: "",
      homePhone: "",
      streetAddress: "",
      city: "",
      state: "",
      zipcode: "",
    },
  });
  const [step, setStep] = useState(1);
  const { user } = useSelector((state) => state.user);
  const { policies } = useSelector((state) => state.data);
  const [policy, setPolicy] = useState();
  const [party, setParty] = useState();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (policies && user) {
      setPolicy(policies.find((x) => x.contractID === user.contractId));
    }
  }, [policies, user]);

  useEffect(() => {
    if (policy) {
      let owners = getAllOwnersFromParties(policy?.parties);
      setParty(owners?.find((x) => x.person?.taxId === user?.identityNumber));
    }
  }, [policy, user]);
  const { csrUser } = useSelector((s) => s.csrUser);

  useEffect(() => {
    setValue("email", user?.emailId ?? party?.email?.address);
    setValue("homePhone", party?.phone?.phoneNumber);
    setValue("mobilePhone", user?.mobile);
    setValue("streetAddress", party?.address?.addressLine1);
    setValue("city", party?.address?.city);
    setValue("state", party?.address?.state);
    setValue("zipcode", party?.address?.postalCode.substring(0, 5));
  }, [party]);

  async function submitUserData(formData) {
    if (step === 1) {
      if (!csrUser?.userId) {
        if (formData.email !== user?.emailId ?? party?.email?.address) {
          setLoading(true);
          await updateUserEmail(
            user?.userId,
            formData.email,
            user.contractId,
            party?.person?.partyTypeCode + party?.person?.personId
          )
            .then((d) => {
              let { emailId: _, ...updatedUser } = user;
              updatedUser.emailId = formData.email;
              dispatch(setUser(updatedUser));
              setLoading(false);
              setStep(2);
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          setLoading(false);
        }
      } else {
        alert("You do not have permission to change user information.");
        setDialogState(false);
      }
    }
  }

  // useEffect(() => {
  //   reset();
  // }, [dialogState]);

  return (
    <Dialog
      open={dialogState}
      onClose={() => setDialogState(false)}
      className="ui-dialogs"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <DialogBackdrop />
      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog"
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {t("ReleaseUserInfoDialog-dialogTitle")}
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            aria-label={t("ReleaseUserInfoDialog-dialogAriaLabel")}
            onClick={() => setDialogState(false)}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <Dialog.Description>
            {t("ReleaseUserInfoDialog-dialogDescription")}
          </Dialog.Description>
          <hr />
          <EverlakeLoadingOverlay isLoading={loading}>
            {step === 1 && (
              <form onSubmit={handleSubmit(submitUserData)}>
                <div className="w-full mx-auto grid grid-cols-1 gap-4 sm:w-4/5 md:w-3/4 lg:w-3/5">
                  {/* Email */}
                  <div>
                    <label htmlFor={`${UUID}-email`} className="block mb-1">
                      {t("DeprecatingUserInfoDialog-form-emailLabel")}
                    </label>
                    <input
                      id={`${UUID}-email`}
                      type="text"
                      // defaultValue={profileCardObj?.email}
                      placeholder={t(
                        "ReleaseUserInfoDialog-form-emailPlaceholder"
                      )}
                      {...register("email", {
                        required: t(
                          "DeprecatingUserInfoDialog-form-emailRequiredText"
                        ),
                        pattern: {
                          value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: "Please enter a valid email",
                        },
                      })}
                    />
                    {errors.email && (
                      <FormInputError
                        id={`${uuid}-emailError`}
                        errorMessage={errors.email.message}
                      />
                    )}
                  </div>
                  {/* phone */}
                  <div>
                    <label htmlFor={`${UUID}-phone`} className="block mb-1">
                      {t("ReleaseUserInfoDialog-form-phoneNumberLabel")}
                    </label>
                    <MaskedPhoneNumberInput
                      name="homePhone"
                      describedby={`${uuid}-phone`}
                      register={register}
                      setValue={setValue}
                      defaultValue={getValues("homePhone")}
                      disabled
                      required={false}
                    />
                  </div>
                  {/* Mobile */}
                  <div>
                    <label htmlFor={`${UUID}-mobile`} className="block mb-1">
                      {t("ReleaseUserInfoDialog-form-mobileNumberLabel")}
                    </label>
                    <MaskedPhoneNumberInput
                      name="mobilePhone"
                      describedby={`${uuid}-mobile`}
                      register={register}
                      setValue={setValue}
                      defaultValue={getValues("mobilePhone")}
                      disabled
                      required={false}
                    />
                  </div>
                  {/* Address */}
                  <div>
                    <label htmlFor={`${UUID}-address`} className="block mb-1">
                      {t("DeprecatingUserInfoDialog-form-addressLabel1")}
                    </label>
                    <input
                      id={`${UUID}-address`}
                      type="text"
                      //defaultValue={profileCardObj?.streetAddress}
                      {...register("streetAddress")}
                      disabled={true}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* City */}
                    <div className="md:col-span-2">
                      <label htmlFor={`${UUID}-city`} className="block mb-1">
                        {t("DeprecatingUserInfoDialog-form-cityLabel")}
                      </label>
                      <input
                        id={`${UUID}-city`}
                        type="text"
                        //defaultValue={profileCardObj?.city}
                        {...register("city")}
                        disabled={true}
                      />
                    </div>
                    {/* State */}
                    <div>
                      <label htmlFor={`${UUID}-state`} className="block mb-1">
                        {t("DeprecatingUserInfoDialog-form-stateLabel")}
                      </label>
                      <select
                        id={`${UUID}-state`}
                        //defaultValue={profileCardObj?.state}
                        {...register("state")}
                        disabled={true}
                      >
                        <option value="">Select</option>
                        {states.map((state, idx) => {
                          return (
                            <option
                              key={`state selection - ${idx}`}
                              value={state.abbreviation}
                            >
                              {state.name}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {/* ZIP */}
                    <div>
                      <label htmlFor={`${UUID}-zip`} className="block mb-1">
                        {t("DeprecatingUserInfoDialog-form-zipCodeLabel")}
                      </label>
                      <input
                        id={`${UUID}-zip`}
                        type="text"
                        //defaultValue={profileCardObj?.zipcode}
                        {...register("zipcode")}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>

                {/* <!-- Buttons --> */}
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => setDialogState(false)}
                  >
                    <Trans
                      i18nKey="ReleaseUserInfoDialog-form-closeButton"
                      components={{ span: <span className="sr-only" /> }}
                    />
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    <Trans
                      i18nKey="ReleaseUserInfoDialog-form-saveButton"
                      components={{ span: <span className="sr-only" /> }}
                    />
                  </button>
                </div>
              </form>
            )}
            {step === 2 && (
              <>
                <div className="p-4 mx-auto md:p-6">
                  <div className="text-xl text-center font-semibold">
                    <Trans
                      i18nKey="DeprecatingUserInfoDialog-form-contactsUpdateMessage"
                      components={{ br: <br /> }}
                    />
                  </div>
                  <div className="text-center mt-4 md:mt-6">
                    <button
                      type="button"
                      className="ui-btn-secondary ui-btn-min-w mt-4"
                      onClick={() => {
                        setStep(1);
                        setDialogState(false);
                      }}
                    >
                      {" "}
                      {t("ReleaseUserInfoDialog-form-closeText")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </EverlakeLoadingOverlay>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
